import { Injectable, OnDestroy, effect, inject, signal } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { LocalStorage } from '@df/browser/storage/decorator/local-storage';
import { IProductDataV2 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { Status } from '@df/core/status';
import { GtmService } from '@df/module-gtm/gtm.service';
import { Customer } from '@df/session/api/customer';
import { CustomerModel } from '@df/session/api/customer.model';
import { Subscription } from 'rxjs';
import { Product } from '../../catalog/product/product.decorator';

declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        interface ICustomerData {
            postcode?: string;
        }
    }
}

export interface IDeliveryPostcodeResponse {
    postcode?: string;
    result?: boolean;
    message?: string;
}

export type TProductDeliveryState = { qty: number; promise?: Promise<number | undefined>; error?: any };
type TMultiSkuResult = { [postcode: string]: { [sku: string]: { has_stock: boolean; branch_stock: number } } };

@Injectable({
    providedIn: 'root'
})
export class DeliveryPostcodeService implements OnDestroy {
    @LocalStorage('deliveryPostcode')
    private postcodeStorage?: string;

    postcode = signal<string | undefined>(this.postcodeStorage);

    private requestService = inject(RequestService);
    private app = inject(App);
    private customerSubscription: Subscription | undefined;
    private dfEventService = inject(DfEventService);
    private gtmService = inject(GtmService);

    private subscriptions = new Subscription();

    /**
     * Stock states per key
     * key is `${productId}.${postcode}`
     * value is stock value
     */
    private productState = new Map<string, TProductDeliveryState>();

    constructor(private customer: Customer) {
        this.customerUpdate();
        effect(() => {
            const postcode = this.postcode();

            if (postcode !== this.postcodeStorage) {
                this.postcodeStorage = postcode;
            }
        });

        // checkout events injectsions
        this.subscriptions.add(
            this.dfEventService.before(event => {
                if (event.id === 'nexusCheckoutSaveShippingAddress') {
                    const postcode = this.postcode();
                    if (postcode && this.hasDeliveryInBasket) {
                        event.data.postcode = postcode;
                    }
                } else if (event.id === 'nexusCheckoutSubmit') {
                    const postcode = this.postcode();
                    if (postcode && this.hasDeliveryInBasket && event.data.shipping_address) {
                        event.data.shipping_address.postcode = postcode;
                    }
                }
            })
        );
    }

    private async customerUpdate() {
        const model = await this.customer.model.load();
        this.onCustomerUpdate(model);

        // check postoce on customer data change
        this.customerSubscription = this.customer.model.obs.data.subscribe(customerModel => {
            this.onCustomerUpdate(customerModel);
        });
    }

    private onCustomerUpdate(customerModel: CustomerModel) {
        if (customerModel?.data && !customerModel.data.postcode && this.postcodeStorage) {
            this.updatePostcode(this.postcodeStorage, undefined, response => {
                this.setPostcode(undefined);
            });
        } else if (customerModel.data.postcode !== this.postcodeStorage) {
            this.setPostcode(customerModel.data.postcode);
        }
    }

    setPostcode(postcode: string | undefined): void {
        this.postcode.set(postcode?.toUpperCase());
        this.productState.clear();
    }

    ngOnDestroy(): void {
        this.customerSubscription?.unsubscribe();
        this.subscriptions.unsubscribe();
    }

    /**
     * Check if basket has delivery-type items
     */
    get hasDeliveryInBasket(): boolean {
        return this.customer.basket.items.some(
            i => !i.data.isCollection || !['true', true].includes(i.data.isCollection as string | boolean)
        );
    }

    /**
     * Check if basket has delivery-type items
     */
    get hasCollectionInBasket(): boolean {
        return this.customer.basket.items.some(
            i => i.data.isCollection && ['true', true].includes(i.data.isCollection as string | boolean)
        );
    }

    async updatePostcode(
        postcode: string,
        onSuccess?: (response: IDeliveryPostcodeResponse) => void,
        onError?: (response: IDeliveryPostcodeResponse) => void,
        status?: Status
    ): Promise<IDeliveryPostcodeResponse> {
        const usedPostcode = postcode.toUpperCase();
        const response = await this.requestService.send(status, {
            path: 'cart/updatedeliverypostcode',
            data: {
                Delivery: usedPostcode
                // productId: this.data.productId
            }
        });
        if (response?.result && response.postcode) {
            this.setPostcode(response.postcode);
            this.trackPostcodeChanegAttempt(usedPostcode, true);
            if (onSuccess) {
                onSuccess(response);
            }
        } else {
            this.trackPostcodeChanegAttempt(usedPostcode, false);
            if (status) {
                status.error = response?.message;
            }

            if (onError) {
                onError(response);
            }
        }

        return response;
    }

    private trackPostcodeChanegAttempt(postcode: string, accepted: boolean): void {
        this.gtmService.sendEventGA4('delivery_postcode_form', {
            postcodeAcceptance: accepted,
            deliveryPostcode: postcode
        });
    }

    /**
     * Retrieves product stock availability for postcode
     */
    async retrieve(product: Product<IProductDataV2>): Promise<number | undefined> {
        const data = product.data;
        const sku = data?.sku;
        if (!sku) {
            return 0;
        }
        if (data.use_consignment_stock && data.consignment_stock) {
            return data.consignment_stock;
        }

        const state = this.productState.get(sku);
        if (state?.promise) {
            return state.promise;
        }
        if (!state || state.error) {
            //   const branches = await this.getAllBranchesIds();
            const promise = new Promise<number | undefined>(async resolve => {
                try {
                    const result = await this.requestService.sendXHR<Record<string, number>>(
                        'stock/by-postcode',
                        { sku: [sku], postcode: this.postcode() },
                        { method: 'GET', api: 'node' }
                    );

                    this.setState(sku, { qty: result[sku] || 0 });

                    if (!result) {
                        this.setState(sku, { qty: 0 });
                    }
                } catch (error) {
                    this.setState(sku, { qty: 0, error });
                }
                resolve(this.productState.get(sku)?.qty);
            });

            this.productState.set(sku, { qty: 0, promise });

            return promise;
        }
        return this.productState.get(sku)?.qty;
    }

    private setState(sku: string, value: TProductDeliveryState): TProductDeliveryState {
        if (!this.productState.has(sku)) {
            this.productState.set(sku, value);
        } else {
            const state = this.productState.get(sku);
            if (state) {
                state.qty = value.qty;
                state.promise = value.promise;
                state.error = value.error;
            }
        }
        return this.productState.get(sku)!;
    }
}
